import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { cn } from "../../utils/ui";

const buttonVariants = cva(
  cn(
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium transition-all outline-none",
    "disabled:cursor-not-allowed",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0",
    "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
    "aria-invalid:ring-destructive/20 aria-invalid:border-destructive"
  ),
  {
    variants: {
      variant: {
        default: cn(
          "bg-primary border-2 border-primary text-primary-foreground hover:bg-primary-700 hover:border-primary-700",
          "disabled:bg-primary-100 disabled:border-primary-100"
        ),
        outline: cn(
          "border-2 border-primary bg-transparent text-primary",
          "hover:bg-primary/20 hover:primary-foreground",
          "disabled:bg-primary-100 disabled:text-primary-foreground disabled:border-primary-100"
        ),
        "simple-outline": cn(
          "border-2 border-border bg-transparent text-foreground",
          "hover:bg-primary/20",
          "disabled:opacity-50 disabled:pointer-events-none"
        ),
        "dark-outline": cn(
          "border-2 border-primary-100 bg-transparent text-primary-100",
          "hover:bg-foreground-dark/10",
          "disabled:opacity-50 disabled:pointer-events-none"
        ),
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive-700",
        "destructive-outline": cn(
          "border-2 border-destructive bg-transparent text-destructive",
          "hover:bg-destructive/20 hover:text-destructive",
          "disabled:bg-destructive-100 disabled:text-destructive-foreground disabled:border-none"
        ),
        accent: cn(
          "bg-accent border-2 border-accent text-accent-foreground hover:bg-accent-600 hover:border-accent-600",
          "disabled:bg-accent-100 disabled:border-accent-100 disabled:text-accent-700"
        ),
        "accent-outline": cn(
          "border-2 border-accent bg-transparent text-accent",
          "hover:bg-accent/20 hover:text-accent",
          "disabled:bg-accent-100 disabled:text-accent-700 disabled:border-none"
        ),
        "accent-subtle": cn(
          "border border-accent-200 bg-accent-50 text-accent",
          "hover:bg-accent-100 hover:text-accent",
          "disabled:bg-accent-50 disabled:text-accent-300 disabled:border-accent-100"
        ),
        ghost: "hover:bg-muted hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "px-6 py-3 gap-2",
        sm: "text-sm gap-1.5 px-5 py-2.5",
        compact:
          "h-8 text-xs gap-1.5 px-3 py-1 [&_svg:not([class*='size-'])]:size-3.5",
        icon: "size-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
      isLoading?: boolean;
    }
>(
  (
    { className, variant, size, asChild = false, isLoading = false, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        ref={ref}
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
        disabled={isLoading || props.disabled}
      >
        {isLoading && <LoadingIcon className="size-4 animate-spin-ccw" />}

        {props.children}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
